import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum DamagePartCategory {
  Front = 'Front',
  Rear = 'Rear',
  Side = 'Side'
}

export interface DamagedPart {
  category: DamagePartCategory;
  name: string;
  selected: boolean;
}

interface DamagedPartsState {
  frontParts: string[];
  rearParts: string[];
  sideParts: string[];
  selectedParts: Record<DamagePartCategory, string[]>;
}

const initialState: DamagedPartsState = {
  frontParts: [
    'Bumper',
    'Grille',
    'Headlight',
    'Hood',
    'Fender',
    'Windshield',
    'Mirror',
    'Wheel',
    'Other'
  ],
  rearParts: [
    'Bumper',
    'Taillight',
    'Trunk/Tailgate',
    'Quarter Panel',
    'Rear Window',
    'Exhaust',
    'Other'
  ],
  sideParts: [
    'Door',
    'Quarter Panel',
    'Rocker Panel',
    'Mirror',
    'Window',
    'Wheel',
    'Other'
  ],
  selectedParts: {
    [DamagePartCategory.Front]: [],
    [DamagePartCategory.Rear]: [],
    [DamagePartCategory.Side]: []
  }
};

const damagedPartsSlice = createSlice({
  name: 'damagedParts',
  initialState,
  reducers: {
    togglePart: (
      state,
      action: PayloadAction<{ category: DamagePartCategory; part: string }>
    ) => {
      const { category, part } = action.payload;
      const categoryParts = state.selectedParts[category];
      const index = categoryParts.indexOf(part);
      
      if (index > -1) {
        categoryParts.splice(index, 1);
      } else {
        categoryParts.push(part);
      }
    },
    selectAllParts: (state, action: PayloadAction<DamagePartCategory>) => {
      const category = action.payload;
      let allParts: string[] = [];
      
      switch (category) {
        case DamagePartCategory.Front:
          allParts = [...state.frontParts];
          break;
        case DamagePartCategory.Rear:
          allParts = [...state.rearParts];
          break;
        case DamagePartCategory.Side:
          allParts = [...state.sideParts];
          break;
      }
      
      state.selectedParts[category] = allParts;
    },
    clearCategoryParts: (state, action: PayloadAction<DamagePartCategory>) => {
      state.selectedParts[action.payload] = [];
    },
    clearAllParts: (state) => {
      state.selectedParts = {
        [DamagePartCategory.Front]: [],
        [DamagePartCategory.Rear]: [],
        [DamagePartCategory.Side]: []
      };
    },
    setSelectedParts: (
      state,
      action: PayloadAction<Record<DamagePartCategory, string[]>>
    ) => {
      state.selectedParts = action.payload;
    }
  }
});

export const {
  togglePart,
  selectAllParts,
  clearCategoryParts,
  clearAllParts,
  setSelectedParts
} = damagedPartsSlice.actions;

export default damagedPartsSlice.reducer;

// Helper functions
export const getPartsForCategory = (
  state: DamagedPartsState,
  category: DamagePartCategory
): string[] => {
  switch (category) {
    case DamagePartCategory.Front:
      return state.frontParts;
    case DamagePartCategory.Rear:
      return state.rearParts;
    case DamagePartCategory.Side:
      return state.sideParts;
    default:
      return [];
  }
};

export const getSelectedPartsCount = (
  selectedParts: Record<DamagePartCategory, string[]>
): number => {
  return Object.values(selectedParts).reduce(
    (total, parts) => total + parts.length,
    0
  );
};

export const hasSelectedParts = (
  selectedParts: Record<DamagePartCategory, string[]>
): boolean => {
  return getSelectedPartsCount(selectedParts) > 0;
};